import { motion } from "motion/react";
import type { PropsWithChildren } from "react";
import styled from "styled-components";

import type { IStyledAttributes } from "@ryde/shared";
import { greyMui, MOBILE_BREAKPOINT, rydeColors } from "@ryde/theme";

import { MODAL_DIALOG_FILL_CONTENT_HEIGHT_CLASS } from "./modal.constants";

interface IModalContentProps extends PropsWithChildren {
  $showContent?: boolean;
}

export const ModalContentStyled = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "modal-content",
})<IModalContentProps>`
  padding-inline-start: 24px;
  padding-inline-end: 16px;
  padding-top: ${props => (props.$showContent ? "16px" : 0)};
  padding-bottom: ${props => (props.$showContent ? "16px" : 0)};

  .ApprovalReasonDialog-Title {
    font-weight: 700;
    margin-block-end: 10px;

    &.PassengersDialog:not(:first-child) {
      margin-top: 10px;
    }
  }

  .ApprovalReasonDialog-Conflicts {
    color: ${rydeColors.darkBlue};
    margin-inline-start: 20px;
  }
`;

interface IModalContainerStyledProps {
  width?: string;
  $skipCssCenter?: boolean;
}

export const ModalContainerStyled = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "modal-container",
})<IModalContainerStyledProps>`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background-color: white;
  position: absolute;
  /*
   * Centring is the same in both directions, so neither half may flip. The entry animation centres with an
   * inline translateX the RTL plugin never sees; a flipped left put the dialog a full width aside in Hebrew.
   */
  left: 50% /* @noflip */;
  top: 50%;
  transform: ${({ $skipCssCenter }) =>
    $skipCssCenter
      ? "none"
      : "translateX(-50%) translateY(-50%) /* @noflip */"};
  max-height: calc(100vh - 120px);
  overflow-y: auto;
  width: ${props => props.width || "600px"};
  border: 1px solid ${greyMui._300};
  outline: none;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    width: 100%;
    max-height: 100vh;
    min-height: 100vh;
    /* needed for some mobile devices always displaying address bar */
    padding-top: 18px;
  }

  &.${MODAL_DIALOG_FILL_CONTENT_HEIGHT_CLASS} {
    @media (max-width: ${MOBILE_BREAKPOINT}) {
      box-sizing: border-box;
      max-height: 100dvh;
      min-height: 100dvh;
      height: 100dvh;
      overflow: hidden;

      ${ModalContentStyled} {
        flex: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;
      }
    }
  }
`;

export const ModalContainerMotion = motion(ModalContainerStyled);

export const ModalHeaderStyled = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "modal-header",
})`
  min-height: 56px;
  display: flex;
  align-items: center;
  padding-inline-end: 16px;
  padding-inline-start: 24px;
  border-bottom: 1px solid ${greyMui._300};
  h1,
  .title {
    font-weight: 600;
    font-size: 20px;
  }
  svg {
    margin-inline-start: auto;
    cursor: pointer;
  }
`;

export const ModalFooterStyled = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "modal-footer",
})`
  min-height: 56px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-inline-end: 20px;
  margin-top: auto;
  gap: 9px;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    min-height: 125px;
    margin-inline-end: 15px;
  }
`;
